import { get as lodashGet, map, each, isEmpty, pick } from 'lodash';
import moment from 'moment';

import {
  newUser,
  featureIsAvailable,
  featureIsEnabled,
  isNewUser,
  hasRoleLevel,
  hasNoRoles,
  hasCustomRole,
  hasCustomPermission
} from '@core/lib/utils/user';
import { getPackagesMap } from '@core/lib/utils/account';
import userFeatures from '@core/lib/constants/userFeatures';
import { destroyAdapterCache } from '@core/api/instance/middlewares/throttleAdapterEnhancer';
import { getCurrentUser } from '@core/api/account';

function User(Api, Helper, Agent, Storage, $q) {
  'ngInject';

  return {
    // Proxied functions
    newUser,
    hasNoRoles,
    hasCustomRole,
    hasCustomPermission,
    featureIsEnabled,

    // Own functions
    login,
    logout,
    resetPassword,
    confirmPassword,
    confirmUser,
    getCurrent,
    getCurrentForcingUpdate,
    getRevision,
    update,
    updateRoles,
    get,
    remove,
    resetApiToken,
    create,
    getFeaturesForForm,
    getAppsForForm,
    getLocalRolesForSaving,
    hasRole,
    changeEmail,
    resendConfirmationEmail,
    changePassword
  };

  /**
   * Checks if the current user has necessary role level
   *
   * @param {String} role
   * @returns {Promise}
   */
  function hasRole(role) {
    return getCurrent()
      .then(user => hasRoleLevel(user, role));
  }

  /**
   * Resets API token
   *
   * @param {String} password
   * @returns {Promise}
   */
  function resetApiToken(password) {
    return Api.apiToken.update({ password }).$promise;
  }

  /**
   * Gets a user with the given id
   *
   * @param {Number} accountId
   * @param {Number} userId
   * @returns {Promise}
   */
  function get(accountId, userId) {
    return Api.user.get({
      account_id: accountId,
      id: userId
    }).$promise;
  }

  /**
   * Removes a user with the given id
   *
   * @param {Number} accountId
   * @param {Number} userId
   * @returns {Promise}
   */
  function remove(accountId, userId) {
    return Api.user.remove({
      account_id: accountId,
      id: userId
    }).$promise;
  }

  /**
   * Updates a user with the given id
   *
   * @param {Number} accountId
   * @param {User} user
   * @returns {Promise}
   */
  function update(accountId, user) {
    return Api.user.update(
      {
        account_id: accountId,
        id: user.id
      },
      user
    ).$promise;
  }

  /**
   * Updates a user roles with the given id
   *
   * @param {Number} accountId
   * @param {User} user
   * @returns {Promise}
   */
  function updateRoles(accountId, user) {
    return Api.user.update(
      {
        account_id: accountId,
        id: user.id,
        action: 'roles'
      },
      user
    ).$promise;
  }

  /**
   * Confirms the invited new user
   *
   * @param {Object} params
   * @returns {Promise}
   */
  function confirmUser(params) {
    return Api.session.update({ action: 'confirmation' }, params).$promise;
  }

  /**
   * Logins user and saves session on the client
   *
   * @param {Object} params
   * @returns {Promise}
   */
  function login(params) {
    return Api.session.create({ action: 'sign_in' }, params).$promise;
  }

  /**
   * Logouts current user and destroy session on the client as well
   *
   * @returns {Promise}
   */
  function logout() {
    Helper.removeCache();
    Agent.reset();
    destroyAdapterCache();
    Storage.remove(/dashboard-core:/);

    return Api.session.remove({ action: 'sign_out' }).$promise;
  }

  /**
   * Sends user email to reset the password
   *
   * @param {Object} params
   * @returns {Promise}
   */
  function resetPassword(params) {
    return Api.session.create({ action: 'password' }, params).$promise;
  }

  /**
   * Updates the user password
   *
   * @param {Object} params
   * @returns {Promise}
   */
  function confirmPassword(params) {
    return Api.session.update({ action: 'password' }, params).$promise;
  }

  /**
   * Gets the current user (from cache if available)
   *
   * @returns {Promise}
   */
  function getCurrent() {
    return $q.when(getCurrentUser()).then(user => user);
  }

  /**
   * Gets current user ensuring the response is not cached
   *
   * @returns {Promise}
   */
  function getCurrentForcingUpdate() {
    return $q.when(getCurrentUser({ forceUpdate: true })).then(user => user);
  }

  /**
   * Reads the current revision using localStorage as cache
   *
   * @returns {Promise[String]}
   */
  function getRevision() {
    if (__IS_DEV__) {
      return $q.resolve(null);
    }

    const now = moment().valueOf();
    const currentRevision = Storage.get('revision');
    const ttl = lodashGet(currentRevision, 'ttl', 0);
    const version = lodashGet(currentRevision, 'version', null);

    if (version && ttl > now) {
      return $q.resolve(version);
    }

    return Api.revision.get({ t: now }).then(versionInFile => {
      Storage.put('revision', {
        version: versionInFile,
        ttl: moment()
          .add(1, 'm')
          .valueOf()
      });

      return versionInFile;
    });
  }

  /**
   * Creates a new user
   *
   * @param {Object} params
   * @param {Number} accountId
   * @returns {Promise}
   */
  function create(params, accountId) {
    return Api.user.create({
      ...params,
      account_id: accountId
    }).$promise;
  }

  /**
   * Gets features for the user management
   *
   * @param {User} user
   * @param {Account} account
   * @returns {Array}
   */
  function getFeaturesForForm(user, account) {
    const packages = getPackagesMap(account);

    return map(userFeatures, feature => {
      if (featureIsAvailable(account, packages, feature)) {
        const enabled = isNewUser(user)
          ? feature.enabled
          : featureIsEnabled(user, feature.key);

        return {
          ...feature,
          available: true,
          enabled
        };
      }

      return {
        ...feature,
        enabled: false,
        available: false
      };
    });
  }

  /**
   * Gets features with defined role for user management
   *
   * @param {Array} currentFeatures
   * @returns {Array}
   * @private
   */
  function _getFeaturesForSaving(currentFeatures) {
    var features = [];

    each(currentFeatures, function(feature) {
      features.push({
        name: feature.key,
        role: feature.enabled ? 'reader' : 'none'
      });
    });

    return isEmpty(features) ? null : features;
  }

  /**
   * Gets features and apps for particular user in user management
   *
   * @param {Object} access
   * @returns {Object}
   */
  function getLocalRolesForSaving(access) {
    var apps = _getAppsForSaving(access.apps);
    var features = _getFeaturesForSaving(access.features);
    var roles = {};

    if (!isEmpty(features)) {
      roles.features = features;
    }

    if (!isEmpty(apps)) {
      roles.mobile_apps = apps;
    }

    return isEmpty(roles) ? null : roles;
  }

  /**
   * Gets trackers with roles and keywords for user management
   *
   * @param {Array} currentTrackers
   * @returns {Array}
   * @private
   */
  function _getTrackersForSaving(currentTrackers) {
    var trackers = [];

    each(currentTrackers, function(trackerData, trackerToken) {
      var tracker = {
        token: trackerToken,
        role: trackerData.role
      };

      if (!isEmpty(trackerData.keywords)) {
        tracker.keywords = trackerData.keywords;
      }

      trackers.push(tracker);
    });

    return trackers;
  }

  /**
   * Gets trackers for each app used in user management
   *
   * @param {Object} app
   * @returns {Object}
   * @private
   */
  function _getTrackersForForm(app) {
    if (isEmpty(app.trackers)) {
      return null;
    }

    var trackers = {};

    each(app.trackers, function(tracker) {
      trackers[tracker.token] = pick(tracker, ['role', 'keywords']);
    });

    return trackers;
  }

  /**
   * Gets apps with roles and trackers for user management
   *
   * @param {User} user
   * @returns {Object}
   */
  function getAppsForForm(user) {
    var apps = {};

    if (user.local_roles && user.local_roles.mobile_apps) {
      each(user.local_roles.mobile_apps, function(appData) {
        apps[appData.token] = {
          role: appData.role
        };

        if (appData.trackers) {
          apps[appData.token].trackers = _getTrackersForForm(appData);
          apps[appData.token].kpi_access = appData.kpi_access;
        }
      });
    }

    return apps;
  }

  /**
   * Gets apps with role and trackers details for user management
   *
   * @param {Array} currentApps
   * @returns {Array}
   * @private
   */
  function _getAppsForSaving(currentApps) {
    var apps = [];

    each(currentApps, function(appData, appToken) {
      var app = {
        token: appToken,
        role: appData.role
      };

      if (!isEmpty(appData.trackers)) {
        app.trackers = _getTrackersForSaving(appData.trackers);
        app.kpi_access = appData.kpi_access;
      }

      apps.push(app);
    });

    return isEmpty(apps) ? null : apps;
  }

  /**
   * @param {Object} params
   * @param {Number} params.accountId
   * @param {Number} params.id
   * @param {String} params.email
   * @param {String} params.password
   * @returns {Promise}
   */
  function changeEmail(params) {
    const { account_id, id, email, password } = params;

    return Api.user.patch(
      { account_id, id, action: 'change_email' },
      { email, password }
    ).$promise;
  }

  /**
   * @param {Object} params
   * @param {Number} params.accountId
   * @param {Number} params.id
   * @returns {Promise}
   */
  function resendConfirmationEmail({ account_id, id }) {
    return Api.user.patch({
      account_id,
      id,
      action: 'resend_confirmation_email'
    }).$promise;
  }

  /**
   * @param {Object} params
   * @param {Number} params.accountId
   * @param {Number} params.id
   * @param {String} params.password
   * @param {String} params.password_confirmation
   * @param {String} params.current_password
   * @returns {Promise}
   */
  function changePassword(params) {
    const {
      account_id,
      id,
      password,
      password_confirmation,
      current_password
    } = params;

    return Api.user.patch(
      {
        account_id,
        id,
        action: 'change_password'
      },
      {
        password: current_password,
        new_password: password,
        confirmation: password_confirmation
      }
    ).$promise;
  }
}

export { User };
